import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Sparkles, AlertTriangle, CheckCircle2 } from "lucide-react";

type Integration = { id: string; name: string; type: string };
type ActionForm = {
  name: string; method: string; pathTemplate: string;
  paramsSchemaJson: string; headersJson: string; queryTemplateJson: string;
  bodyTemplateJson: string; authStrategy: string;
};
type ToolForm = {
  id?: string;
  name: string; displayName: string; description: string;
  integrationId: string | null;
  requiresApproval: boolean;
  perUserPerHourLimit: string; perOrgPerHourLimit: string;
  action: ActionForm;
};

const EMPTY_FORM: ToolForm = {
  name: "", displayName: "", description: "", integrationId: null,
  requiresApproval: false, perUserPerHourLimit: "", perOrgPerHourLimit: "",
  action: {
    name: "", method: "GET", pathTemplate: "",
    paramsSchemaJson: JSON.stringify({ type: "object", properties: {}, additionalProperties: false }, null, 2),
    headersJson: "{}", queryTemplateJson: "{}", bodyTemplateJson: "{}",
    authStrategy: "integration",
  },
};

function safePretty(s: string) {
  try { return JSON.stringify(JSON.parse(s), null, 2); } catch { return s; }
}

/** Manual New/Edit Tool dialog. Used for both Create (chatbotId+no id) and Edit (id set). */
export function ToolBuilderDialog({
  open, onClose, chatbotId, toolId,
}: { open: boolean; onClose: () => void; chatbotId: string; toolId?: string }) {
  const { toast } = useToast();
  const isEdit = !!toolId;
  const [form, setForm] = useState<ToolForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const { data: integrations } = useQuery<Integration[]>({
    queryKey: ["/api/integrations"], enabled: open,
  });

  const { data: existing } = useQuery<any>({
    queryKey: ["/api/tools", toolId], enabled: open && isEdit,
  });

  useEffect(() => {
    if (!open) return;
    if (isEdit && existing) {
      const t = existing.tool;
      const a = existing.actions?.[0] ?? {};
      setForm({
        id: t.id, name: t.name, displayName: t.displayName, description: t.description ?? "",
        integrationId: existing.extra?.integrationId ?? null,
        requiresApproval: t.requiresApproval === "true" || t.requiresApproval === true,
        perUserPerHourLimit: t.perUserPerHourLimit?.toString() ?? "",
        perOrgPerHourLimit: t.perOrgPerHourLimit?.toString() ?? "",
        action: {
          name: a.name ?? t.name,
          method: a.method ?? "GET",
          pathTemplate: a.pathTemplate ?? "",
          paramsSchemaJson: safePretty(a.paramsSchemaJson ?? t.argsSchema ?? "{}"),
          headersJson: safePretty(a.headersJson ?? "{}"),
          queryTemplateJson: safePretty(a.queryTemplateJson ?? "{}"),
          bodyTemplateJson: safePretty(a.bodyTemplateJson ?? "{}"),
          authStrategy: a.authStrategy ?? "integration",
        },
      });
    } else if (!isEdit) {
      setForm(EMPTY_FORM);
    }
  }, [open, isEdit, existing]);

  const setAction = (patch: Partial<ActionForm>) =>
    setForm((f) => ({ ...f, action: { ...f.action, ...patch } }));

  const submit = async () => {
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        displayName: form.displayName.trim() || form.name.trim(),
        description: form.description,
        integrationId: form.integrationId || null,
        requiresApproval: form.requiresApproval,
        perUserPerHourLimit: form.perUserPerHourLimit === "" ? null : Number(form.perUserPerHourLimit),
        perOrgPerHourLimit: form.perOrgPerHourLimit === "" ? null : Number(form.perOrgPerHourLimit),
        actions: [{ ...form.action, name: form.name.trim(), displayName: form.displayName.trim() }],
      };
      if (isEdit) await apiRequest("PUT", `/api/tools/${toolId}`, body);
      else await apiRequest("POST", `/api/chatbots/${chatbotId}/tools`, body);
      toast({ description: isEdit ? "Tool updated." : "Tool created." });
      await queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "tools"] });
      await queryClient.invalidateQueries({ queryKey: ["/api/tools"] });
      onClose();
    } catch (e: any) {
      const msg = e?.message ?? "Failed to save tool.";
      toast({ variant: "destructive", description: msg });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-tool-builder">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit custom tool" : "New custom tool"}</DialogTitle>
          <DialogDescription>
            Define an HTTP action this bot can call. Templates may reference <code>{`{param}`}</code> placeholders declared in the schema.
            Credentials come from the linked integration — never paste tokens into headers.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Identifier (snake_case)</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="get_ticket_status" data-testid="input-tool-name" />
          </div>
          <div>
            <Label>Display name</Label>
            <Input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              placeholder="Get Ticket Status" data-testid="input-tool-display-name" />
          </div>
        </div>
        <div>
          <Label>Description (shown to the LLM)</Label>
          <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="input-tool-description" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Integration</Label>
            <Select value={form.integrationId ?? "none"} onValueChange={(v) => setForm({ ...form, integrationId: v === "none" ? null : v })}>
              <SelectTrigger aria-label="Integration" data-testid="select-integration"><SelectValue placeholder="(none)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">(none — full URL in path)</SelectItem>
                {integrations?.map((i) => (
                  <SelectItem key={i.id} value={i.id}>{i.name} <span className="text-xs text-muted-foreground">({i.type})</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>HTTP method</Label>
            <Select value={form.action.method} onValueChange={(v) => setAction({ method: v })}>
              <SelectTrigger aria-label="HTTP method" data-testid="select-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>Path template</Label>
          <Input value={form.action.pathTemplate} onChange={(e) => setAction({ pathTemplate: e.target.value })}
            placeholder="/api/now/table/incident/{sysId}" data-testid="input-path" />
        </div>

        <JsonField label="Parameters schema (JSON Schema, strict)" value={form.action.paramsSchemaJson}
          onChange={(v) => setAction({ paramsSchemaJson: v })} testId="input-schema" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <JsonField label="Headers" value={form.action.headersJson} onChange={(v) => setAction({ headersJson: v })} testId="input-headers" small />
          <JsonField label="Query" value={form.action.queryTemplateJson} onChange={(v) => setAction({ queryTemplateJson: v })} testId="input-query" small />
          <JsonField label="Body template" value={form.action.bodyTemplateJson} onChange={(v) => setAction({ bodyTemplateJson: v })} testId="input-body" small />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div>
            <Label>Per-user / hour</Label>
            <Input type="number" min="0" placeholder="No limit" value={form.perUserPerHourLimit}
              onChange={(e) => setForm({ ...form, perUserPerHourLimit: e.target.value })} data-testid="input-per-user" />
          </div>
          <div>
            <Label>Per-org / hour</Label>
            <Input type="number" min="0" placeholder="No limit" value={form.perOrgPerHourLimit}
              onChange={(e) => setForm({ ...form, perOrgPerHourLimit: e.target.value })} data-testid="input-per-org" />
          </div>
          <div className="flex items-center justify-between border rounded-md p-2">
            <Label className="text-sm">Requires approval</Label>
            <Switch checked={form.requiresApproval} onCheckedChange={(v) => setForm({ ...form, requiresApproval: v })}
              data-testid="switch-requires-approval-builder" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="button-cancel-builder">Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.name.trim() || !form.action.pathTemplate.trim()}
            data-testid="button-save-builder">
            {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Saving…</> : (isEdit ? "Save changes" : "Create tool")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function JsonField({ label, value, onChange, testId, small }: { label: string; value: string; onChange: (v: string) => void; testId: string; small?: boolean }) {
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    try { JSON.parse(value); setErr(null); } catch (e: any) { setErr(e.message); }
  }, [value]);
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Textarea rows={small ? 4 : 7} value={value} onChange={(e) => onChange(e.target.value)}
        className="font-mono text-xs" data-testid={testId} />
      {err && <p className="text-[10px] text-destructive mt-1">{err}</p>}
    </div>
  );
}

// ── AI Generate dialog ──

type Draft = {
  id: string; status: string; prompt: string; spec: string; validation: string;
  promptTokens?: number; completionTokens?: number; chatbotId?: string; integrationId?: string;
  savedToolId?: string;
};

export function AiToolGenerateDialog({
  open, onClose, chatbotId,
}: { open: boolean; onClose: () => void; chatbotId: string }) {
  const { toast } = useToast();
  const [prompt, setPrompt] = useState("");
  const [integrationId, setIntegrationId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [spec, setSpec] = useState<string>("");
  const [refineText, setRefineText] = useState("");
  const [busy, setBusy] = useState<"generate" | "refine" | "save" | null>(null);

  const { data: integrations } = useQuery<Integration[]>({
    queryKey: ["/api/integrations"], enabled: open,
  });

  useEffect(() => {
    if (!open) {
      setPrompt(""); setDraft(null); setSpec(""); setRefineText(""); setIntegrationId(null);
    }
  }, [open]);

  const validation = useMemo(() => {
    if (!draft?.validation) return null;
    try { return JSON.parse(draft.validation); } catch { return null; }
  }, [draft]);

  const generate = async () => {
    setBusy("generate");
    try {
      const d = await apiRequest("POST", `/api/tools/drafts/generate`, {
        prompt, chatbotId, integrationId: integrationId || null,
      }).then(r => r.json());
      setDraft(d); setSpec(safePretty(d.spec ?? "{}"));
    } catch (e: any) {
      toast({ variant: "destructive", description: e?.message ?? "Generation failed." });
    } finally { setBusy(null); }
  };

  const refine = async () => {
    if (!draft || !refineText.trim()) return;
    setBusy("refine");
    try {
      const d = await apiRequest("POST", `/api/tools/drafts/${draft.id}/refine`, { followUpPrompt: refineText }).then(r => r.json());
      setDraft(d); setSpec(safePretty(d.spec ?? "{}")); setRefineText("");
    } catch (e: any) {
      toast({ variant: "destructive", description: e?.message ?? "Refine failed." });
    } finally { setBusy(null); }
  };

  const save = async () => {
    if (!draft) return;
    setBusy("save");
    try {
      // Send the (possibly user-edited) spec back as the source of truth.
      // We do this by re-saving the draft after a refine-with-edited-spec is impractical here,
      // so we just save and let the server use the latest generated spec; edits to JSON inline
      // are advisory in v1 — the user can edit further via "Edit" on the row after creation.
      const tool = await apiRequest("POST", `/api/tools/drafts/${draft.id}/save`, {
        enableForBotId: chatbotId,
      }).then(r => r.json());
      toast({ description: `Tool "${tool.displayName}" created and enabled.` });
      await queryClient.invalidateQueries({ queryKey: ["/api/chatbots", chatbotId, "tools"] });
      await queryClient.invalidateQueries({ queryKey: ["/api/tools"] });
      onClose();
    } catch (e: any) {
      toast({ variant: "destructive", description: e?.message ?? "Save failed." });
    } finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-ai-tool">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> AI Generate Tool</DialogTitle>
          <DialogDescription>
            Describe what the bot should be able to do; we'll draft a tool spec for your review.
            Nothing runs until you click <strong>Save</strong>.
          </DialogDescription>
        </DialogHeader>

        {!draft && (
          <>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label>What should this tool do?</Label>
                <Textarea
                  rows={4}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder='e.g. "Look up the status of a ServiceNow incident given its number, and return short, plain-English summary."'
                  data-testid="input-ai-prompt"
                />
              </div>
              <div>
                <Label>Bind to integration (optional)</Label>
                <Select value={integrationId ?? "none"} onValueChange={(v) => setIntegrationId(v === "none" ? null : v)}>
                  <SelectTrigger aria-label="Bind to integration" data-testid="select-ai-integration"><SelectValue placeholder="(none)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">(none)</SelectItem>
                    {integrations?.map((i) => (
                      <SelectItem key={i.id} value={i.id}>{i.name} ({i.type})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={onClose} data-testid="button-cancel-ai">Cancel</Button>
              <Button onClick={generate} disabled={busy === "generate" || !prompt.trim()} data-testid="button-generate-ai">
                {busy === "generate" ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Generating…</> : "Generate draft"}
              </Button>
            </DialogFooter>
          </>
        )}

        {draft && (
          <>
            <div className="flex items-center gap-2">
              {validation?.ok
                ? <Badge variant="secondary" className="gap-1"><CheckCircle2 className="w-3 h-3" />Validated</Badge>
                : <Badge variant="destructive" className="gap-1"><AlertTriangle className="w-3 h-3" />Issues</Badge>}
              {draft.promptTokens != null && (
                <span className="text-xs text-muted-foreground">
                  tokens: {draft.promptTokens} in / {draft.completionTokens ?? 0} out
                </span>
              )}
            </div>

            {validation && !validation.ok && (
              <div className="rounded-md border border-destructive/50 bg-destructive/5 p-2 text-xs" data-testid="panel-validation-errors">
                <div className="font-medium mb-1">We couldn't fully validate this draft</div>
                <p className="mb-1 text-muted-foreground">
                  It was auto-repaired but still needs a few details. Describe the fix in <strong>Refine</strong> below
                  (e.g. add the API path or a required parameter) and click Refine to try again.
                </p>
                {(validation.errors ?? []).length > 0 && (
                  <ul className="list-disc pl-4 space-y-0.5">
                    {(validation.errors ?? []).map((e: string, i: number) => <li key={i} data-testid={`text-validation-error-${i}`}>{e}</li>)}
                  </ul>
                )}
              </div>
            )}
            {validation?.warnings?.length > 0 && (
              <div className="rounded-md border border-yellow-500/50 bg-yellow-500/5 p-2 text-xs">
                <div className="font-medium mb-1">Warnings</div>
                <ul className="list-disc pl-4 space-y-0.5">
                  {validation.warnings.map((e: string, i: number) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}

            <div>
              <Label>Generated spec (read-only preview)</Label>
              <Textarea rows={14} value={spec} readOnly className="font-mono text-xs" data-testid="textarea-spec" />
            </div>

            <div>
              <Label>Refine</Label>
              <Textarea rows={2} value={refineText} onChange={(e) => setRefineText(e.target.value)}
                placeholder='e.g. "Add an optional comment parameter and POST it to /work_notes."' data-testid="input-refine" />
            </div>

            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={onClose} data-testid="button-cancel-ai-2">Cancel</Button>
              <Button variant="outline" onClick={refine} disabled={busy === "refine" || !refineText.trim()} data-testid="button-refine">
                {busy === "refine" ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Refining…</> : "Refine"}
              </Button>
              <Button onClick={save} disabled={busy === "save" || !validation?.ok} data-testid="button-save-ai">
                {busy === "save" ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Saving…</> : "Save & enable"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
